import {
  BoundMultiSelectField,
  BoundNumberField,
  BoundSelectField,
  BoundTextField,
  Css,
  FocusedFormLayout,
  FormSectionLayout,
  useComputed,
  useSnackbar,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { useNavigate } from "react-router-dom";
import { createPlanPackagesUrl } from "src/RouteUrls";
import { Maybe, ReadyPlanStatus, ReadyPlanType, SavePlanPackageInput } from "src/generated/graphqlTypes";
import { useMarketFilter } from "src/hooks";
import { useCreatePlanPackageMutation } from "src/routes/libraries/plan-package-v2/create/CreatePlanPackagePage.generated";
import { planPackageOverviewRoute } from "src/routing/domains/librariesPaths";

export function CreatePlanPackagePage() {
  const navigate = useNavigate();
  const { triggerNotice } = useSnackbar();
  const { options: markets } = useMarketFilter();
  const [create] = useCreatePlanPackageMutation();
  const formState = useFormState({ config: formConfig });
  const canCreate = useComputed(() => formState.valid, [formState]);

  async function onCreate() {
    const { code, ...rest } = formState.value;
    const { data } = await create({
      variables: { input: { ...rest, status: ReadyPlanStatus.Draft, code: code?.toString().padStart(3, "0") } },
    });
    const { id, name, version } = data!.savePlanPackage.planPackage;
    triggerNotice({ message: `Plan package: ${name} successfully created`, icon: "success" });
    navigate(planPackageOverviewRoute.url({ planPackageId: id, planPackageVersionId: version.id }));
  }

  return (
    <FocusedFormLayout
      title="Create a Plan Package"
      breadcrumbs={{ breadcrumbs: { label: "Plan Library", href: createPlanPackagesUrl() } }}
      onCancel={() => navigate(createPlanPackagesUrl())}
      completeLabel="Create"
      onComplete={onCreate}
      primaryDisabled={!canCreate}
    >
      <FormSectionLayout
        // The workflow header carries the title, so the form-level title is empty
        title=""
        sections={[
          {
            title: "Plan Name",
            description: `Use a permanent name if you have one. Otherwise, use a temporary one like "Plan 1", and swap in the final name once it's ready.`,
            fields: (
              <BoundTextField label="Plan Name" field={formState.name} placeholder="i.e. The Everett" fullWidth />
            ),
          },
          {
            title: "Plan Code",
            description:
              "Choose a plan type and enter a plan number. Plan numbers are based on net area and are typically added once area is locked in at CDs. Leave blank if not yet known.",
            fields: (
              <div css={Css.dg.gtc("130px 1fr").gap2.$}>
                <BoundSelectField
                  label="Plan Type"
                  field={formState.type}
                  options={Object.values(ReadyPlanType).map((code) => ({ id: code, name: code }))}
                  fullWidth
                />
                <BoundNumberField
                  label="Plan Number"
                  field={formState.code}
                  placeholder="Plan #"
                  numIntegerDigits={4}
                  useGrouping={false}
                  fullWidth
                />
              </div>
            ),
          },
          {
            title: "Market",
            description: "Select the markets where this plan will be available to create a product offering.",
            fields: <BoundMultiSelectField label="Market(s)" field={formState.markets} options={markets} fullWidth />,
          },
        ]}
      />
    </FocusedFormLayout>
  );
}

/** `code` is a `String` on the input, but the design edits it as a plain number. */
type CreatePlanPackageForm = Pick<SavePlanPackageInput, "name" | "type" | "markets"> & { code: Maybe<number> };

const formConfig: ObjectConfig<CreatePlanPackageForm> = {
  name: { type: "value", rules: [required] },
  type: { type: "value", rules: [required] },
  code: { type: "value" },
  markets: { type: "value" },
};
